/**
 * gps-export.ts — KML + CSV export for the GPS breadcrumb trail (Phase 8 / Task 8.5).
 *
 * Pure string builders (no DOM) + a small browser download helper. KML is
 * Google-Earth compatible; CSV is lat/lng/time/event per §15.
 */

import type { GpsTrailPoint } from './use-completed-jobs';

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function csvCell(v: string | number | null): string {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV: one row per point (captured_at, lat, lng, accuracy_ft, speed_mph, heading_deg, event_type, event_label). */
export function trailToCsv(points: readonly GpsTrailPoint[]): string {
  const header = 'captured_at,lat,lng,accuracy_ft,speed_mph,heading_deg,event_type,event_label';
  const rows = points.map((p) =>
    [
      csvCell(p.capturedAt),
      csvCell(p.lat),
      csvCell(p.lng),
      csvCell(p.accuracyFt),
      csvCell(p.speedMph),
      csvCell(p.headingDeg),
      csvCell(p.eventType),
      csvCell(p.eventLabel),
    ].join(','),
  );
  return [header, ...rows].join('\n');
}

/** KML: a LineString route + a Placemark per non-POSITION event point. */
export function trailToKml(points: readonly GpsTrailPoint[], name: string): string {
  const valid = points.filter((p) => p.lat != null && p.lng != null);
  const coords = valid.map((p) => `${String(p.lng)},${String(p.lat)},0`).join(' ');
  const placemarks = valid
    .filter((p) => p.eventType !== 'POSITION')
    .map(
      (p) =>
        `<Placemark><name>${escapeXml(p.eventType)}</name>` +
        `<Point><coordinates>${String(p.lng)},${String(p.lat)},0</coordinates></Point></Placemark>`,
    )
    .join('');
  return (
    `<?xml version="1.0" encoding="UTF-8"?>` +
    `<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${escapeXml(name)}</name>` +
    `<Placemark><name>Route</name><LineString><tessellate>1</tessellate>` +
    `<coordinates>${coords}</coordinates></LineString></Placemark>` +
    `${placemarks}</Document></kml>`
  );
}

/** Trigger a browser download of `content` as `filename`. */
export function downloadFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
